import { Injectable } from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  of,
  interval,
  Subject,
  merge,
  filter,
  combineLatest,
} from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import {
  AddOrUpdatePersistenceInput,
  SubTenant,
} from 'src/app/graphql/generated';
import { WorkflowStatusCollateralStringTypes } from 'src/app/secure/shipping/types/collateral-filtering.types';
import { LabelValue } from 'src/app/shared/models/label-value.interface';
import { GraphqlApisService } from 'src/app/shared/services/graphql-apis/graphql-apis.service';
import { PersistenceGraphqlApisService } from 'src/app/shared/services/graphql-apis/persistence-graphql-apis.service';
import {
  CollateralWorkflowResponse,
  FundingWorkflowResponse,
  SettlementWorkflowResponse,
  WorkflowGraphqlApisService,
} from 'src/app/shared/services/graphql-apis/workflow-graphql-apis.service';
import { UserService } from 'src/app/shared/services/user.service';

@Injectable({
  providedIn: 'root',
})
export class LeftSidebarMenuService {
  private refetchWorkflowCounts$ = new Subject<void>();

  /**
   * selected subtenant ids populated from the multi-select dropdown value change event in LeftSidebarMenuComponent
   */
  private selectedSubTenantIdsSource: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);

  private isSidebarExpandedSource: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  private isOnAdminRouteSource$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public selectedSubTenantIds$ = this.selectedSubTenantIdsSource.asObservable();

  /**
   * refresh interval for the left sidebar menu to refresh the data every 30 seconds
   * only active when the sidebar is expanded and not on the admin route
   */
  private refreshInterval$ = combineLatest([
    this.isSidebarExpandedSource,
    this.isOnAdminRouteSource$,
  ]).pipe(
    map(([isExpanded, isAdmin]) =>
      isExpanded && !isAdmin ? interval(30000) : of(null)
    ),
    switchMap((result) => result),
    filter((val): val is number => val !== null)
  );

  /**
   * all subtenants sorted by originator name alphabetically
   */
  public allSubTenants$: Observable<LabelValue[]> = this.graphqlApiService
    .getAllSubTenants()
    .pipe(
      map(({ data: { subTenants } }) => {
        return subTenants
          .map((subTenant: SubTenant) => ({
            label: subTenant.originatorName,
            value: subTenant.subTenantId,
          }))
          .sort((a: LabelValue, b: LabelValue) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          );
      })
    );

  /**
   * grabs initial funding status count and then refreshes it every 30 seconds
   * also refreshes count when the selected subtenant ids change in the multi-select dropdown
   */
  public fundingStatusCount$ = this.selectedSubTenantIds$.pipe(
    switchMap((subTenantIds) => {
      const triggers$ = merge(
        // triggers the workflow count fetch every 30 seconds
        this.refreshInterval$,
        // triggers the workflow count fetch when the selected subtenant ids change
        this.refetchWorkflowCounts$.asObservable()
      );

      return triggers$.pipe(
        switchMap(() =>
          this.workflowGraphqlApisService.getFundingWorkflow(subTenantIds).pipe(
            map(({ data: { fundingWorkflow } }) =>
              fundingWorkflow?.filter((w) => w.count !== 0)
            ),
            map((fundingWorkflow) =>
              this.filterFundingWorkflowBasedOnUserType(fundingWorkflow)
            ),
            catchError((error) => {
              console.error('Error fetching funding workflow:', error);
              return of([]);
            })
          )
        )
      );
    })
  );

  /**
   * grabs initial settlement status count and then refreshes it every 30 seconds
   * also refreshes count when the selected subtenant ids change in the multi-select dropdown
   */
  public settlementStatusCount$ = this.selectedSubTenantIds$.pipe(
    switchMap((subTenantIds) => {
      const triggers$ = merge(
        // triggers the workflow count fetch every 30 seconds
        this.refreshInterval$,
        // triggers the workflow count fetch when the selected subtenant ids change
        this.refetchWorkflowCounts$.asObservable()
      );

      return triggers$.pipe(
        switchMap(() =>
          this.workflowGraphqlApisService
            .getSettlementWorkflow(subTenantIds)
            .pipe(
              map(({ data: { settlementWorkflow } }) =>
                this.filterSettlementWorkflowBasedOnUserType(settlementWorkflow)
              ),
              map((settlementWorkflow) => {
                return Object.entries(settlementWorkflow)
                  ?.filter(
                    ([key, count]) => key !== '__typename' && count !== 0
                  )
                  ?.map(([status, count]) => ({
                    status: this.mapSettlementStatusKeys(status),
                    workflowStatus: status,
                    count,
                  }));
              }),
              catchError((error) => {
                console.error('Error fetching settlement workflow:', error);
                return of([]);
              })
            )
        )
      );
    })
  );

  /**
   * grabs initial collateral status count and then refreshes it every 30 seconds
   * also refreshes count when the selected subtenant ids change in the multi-select dropdown
   */
  public collateralStatusCount$ = this.selectedSubTenantIds$.pipe(
    switchMap((subTenantIds) => {
      const triggers$ = merge(
        // triggers the workflow count fetch every 30 seconds
        this.refreshInterval$,
        // triggers the workflow count fetch when the selected subtenant ids change
        this.refetchWorkflowCounts$.asObservable()
      );

      return triggers$.pipe(
        switchMap(() =>
          this.workflowGraphqlApisService
            .getCollateralWorkflow(subTenantIds)
            .pipe(
              map(({ data: { collateralWorkflow } }) =>
                this.filterCollateralWorkflowBasedOnUserType(collateralWorkflow)
              ),
              map((collateralWorkflow) =>
                collateralWorkflow
                  ?.map((w) => ({
                    status: this.mapCollateralStatusKeys(w.status),
                    count: w.count,
                    workflowStatus: this.mapCollateralWorkflowStatusKeys(
                      w.status
                    ),
                    eNote: w.status === 'Ready to Ship (eNote)' ? true : false,
                  }))
                  ?.filter((w) => w.count !== 0)
              ),
              catchError((error) => {
                console.error('Error fetching collateral workflow:', error);
                return of([]);
              })
            )
        )
      );
    })
  );

  constructor(
    private workflowGraphqlApisService: WorkflowGraphqlApisService,
    private userService: UserService,
    private graphqlApiService: GraphqlApisService,
    private persistenceService: PersistenceGraphqlApisService
  ) {}

  public getIsSidebarExpanded$(): Observable<boolean> {
    return this.isSidebarExpandedSource.asObservable();
  }

  public getIsOnAdminRoute$(): Observable<boolean> {
    return this.isOnAdminRouteSource$.asObservable();
  }

  public setIsOnAdminRoute(isOnAdminRoute: boolean): void {
    this.isOnAdminRouteSource$.next(isOnAdminRoute);
  }

  public toggleSidebar(): void {
    this.isSidebarExpandedSource.next(!this.isSidebarExpandedSource.value);
  }

  public setSelectedSubTenantIds(subTenantIds: string[]) {
    this.selectedSubTenantIdsSource.next(subTenantIds);
    this.refetchWorkflowCounts$.next();
    if (this.userService.isTenant) {
      this.setSelectedSubTenantsInPersistence(subTenantIds);
    }
  }

  private mapSettlementStatusKeys(settlementWorkflowType: string) {
    switch (settlementWorkflowType) {
      case 'fundsReceived':
        return 'Funds Received';
      case 'settlementRequest':
        return 'Settlement Request';
      case 'pendingConfirmation':
        return 'Pending Confirmation';
      case 'returned':
        return 'Returned';
      case 'unIdentified':
        return 'Unidentified';
      default:
        return settlementWorkflowType;
    }
  }

  private mapCollateralStatusKeys(collateralWorkflowType: string) {
    switch (collateralWorkflowType) {
      case 'WetLoans':
        return 'Wet';
      default:
        return collateralWorkflowType;
    }
  }

  private mapCollateralWorkflowStatusKeys(collateralWorkflowStatus: string) {
    switch (collateralWorkflowStatus) {
      case 'WetLoans':
        return WorkflowStatusCollateralStringTypes.WET;
      case 'In Review':
        return WorkflowStatusCollateralStringTypes.IN_REVIEW;
      case 'Pending Investor':
        return WorkflowStatusCollateralStringTypes.PENDING_INVESTOR;
      case 'Pending Shipping Instructions':
        return WorkflowStatusCollateralStringTypes.PENDING_SHIPPING_INSTRUCTIONS;
      case 'Collateral Returned':
        return WorkflowStatusCollateralStringTypes.COLLATERAL_RETURNED;
      case 'Ready to Ship':
        return WorkflowStatusCollateralStringTypes.READY_TO_SHIP;
      case 'Ready to Ship (eNote)':
        return WorkflowStatusCollateralStringTypes.READY_TO_SHIP;
      case 'Not Accepted':
        return WorkflowStatusCollateralStringTypes.NOT_ACCEPTED;
      default:
        return collateralWorkflowStatus;
    }
  }

  private filterFundingWorkflowBasedOnUserType(
    fundingWorkflow: FundingWorkflowResponse[]
  ): FundingWorkflowResponse[] {
    if (this.userService.isTenant) {
      return fundingWorkflow.filter(
        (w) =>
          w.status === 'Pending Approval' ||
          w.status === 'Approved' ||
          w.status === 'Processing'
      );
    } else if (this.userService.isSubTenant) {
      return fundingWorkflow.filter(
        (w) => w.status === 'New' || w.status === 'Returned'
      );
    }
    return fundingWorkflow;
  }

  private filterSettlementWorkflowBasedOnUserType(
    settlementWorkflow: SettlementWorkflowResponse
  ): Partial<SettlementWorkflowResponse> {
    const {
      subTenantIds,
      tenantId,
      ...settlementWorkflowWithoutSubTenantIdsAndTenantId
    } = settlementWorkflow;
    if (this.userService.isSubTenant) {
      const { unIdentified, pendingConfirmation, ...rest } =
        settlementWorkflowWithoutSubTenantIdsAndTenantId;
      return rest;
    } else if (this.userService.isTenant) {
      return settlementWorkflowWithoutSubTenantIdsAndTenantId;
    }
    return settlementWorkflow;
  }

  private filterCollateralWorkflowBasedOnUserType(
    collateralWorkflow: CollateralWorkflowResponse[]
  ): CollateralWorkflowResponse[] {
    if (this.userService.isTenant) {
      return collateralWorkflow.filter(
        (w) =>
          w.status === 'WetLoans' ||
          w.status === 'In Review' ||
          w.status === 'Pending Investor' ||
          w.status === 'Pending Shipping Instructions' ||
          w.status === 'Collateral Returned' ||
          w.status === 'Ready to Ship' ||
          w.status === 'Ready to Ship (eNote)' ||
          w.status === 'Not Accepted'
      );
    } else if (this.userService.isSubTenant) {
      return collateralWorkflow.filter(
        (w) =>
          w.status === 'WetLoans' ||
          w.status === 'In Review' ||
          w.status === 'Pending Investor' ||
          w.status === 'Pending Shipping Instructions' ||
          w.status === 'Collateral Returned'
      );
    }
    return collateralWorkflow;
  }

  public getSelectedOriginatorsFromPersistence$(): Observable<string[]> {
    return this.persistenceService
      .getPersistenceSettings({
        origin: 'User',
        originId: this.userService.claims.userId || '',
        originType: 'GLOBAL',
      })
      .pipe(
        map(({ data: { persistence } }) => {
          if (persistence && persistence.length) {
            return (
              JSON.parse(persistence[0]?.persistenceJson)
                ?.selectedOriginators || []
            );
          }
          return [];
        })
      );
  }

  /**
   * Sets the selected subtenant ids in the persistence settings
   * this is called when the selected subtenant ids change in the multi-select dropdown
   * as a side effect inside the setSelectedSubTenantIds method
   */
  private setSelectedSubTenantsInPersistence(subTenantIds: string[]) {
    const input: AddOrUpdatePersistenceInput = {
      origin: 'User',
      originId: this.userService.claims.userId,
      originType: 'GLOBAL',
      persistenceJson: JSON.stringify({ selectedOriginators: subTenantIds }),
    };
    this.persistenceService.addOrUpdatePersistence(input).subscribe();
  }
}
